import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowDownTrayIcon, DocumentTextIcon, ClipboardDocumentListIcon } from '@heroicons/react/24/outline';
import * as XLSX from 'xlsx';
import moment from 'moment';
import GraphqlService from '../../service/graphqlService';

export default function LastUsersAction() {
  const [lastActionBeforeDate, setLastActionBeforeDate] = useState(moment().subtract(7, 'days').format('YYYY-MM-DD'));

  const {
    data: usersData,
    error: errorFetching,
    isLoading,
  } = useQuery(['lastUsersAction'],
    async () => GraphqlService.getLastUsersAction(), {
    cacheTime: 300000,
    staleTime: 300000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
  });

  const filterUsersData = (data) => {
    if (!data) return [];
    return data.filter((user) => moment(user.lastAction).isBefore(lastActionBeforeDate));
  };

  const downloadCsv = () => {
    const filteredData = filterUsersData(usersData);
    if (!filteredData || !filteredData.length) return;

    let csvContent = 'User, Email,Last Action Date\n';
    filteredData.forEach((user) => {
      const row = `${user.user},${user.cognitoEmail},${user.lastAction}`;
      csvContent += `${row}\n`;
    });

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'LastUsersAction.csv');
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
  };

  const downloadXls = () => {
    const filteredData = filterUsersData(usersData);
    if (!filteredData || !filteredData.length) return;

    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Users');

    const xlsBuffer = XLSX.write(workbook, { bookType: 'xls', type: 'array' });
    const blob = new Blob([xlsBuffer], { type: 'application/vnd.ms-excel' });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'LastUsersAction.xls');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const copyUsersInClipboard = () => {
    const filteredData = filterUsersData(usersData);
    if (!filteredData) return;
    const emails = filteredData.map((user) => user.cognitoEmail).join(';');
    navigator.clipboard.writeText(emails);
  };

  function renderContent() {
    if (errorFetching) {
      return (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mt-4" role="alert">
          <p className="font-bold">Error</p>
          <p>{errorFetching.message}</p>
        </div>
      );
    }

    if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
          <p className="text-gray-700">Retrieving last users action, please wait...</p>
        </div>
      );
    }

    const filteredData = filterUsersData(usersData);

    if (filteredData.length > 0) {
      return (
        <div className="mt-6">
          <div className="flex flex-wrap justify-end items-center mb-4 space-x-2">
            <button
              id="btnCSV"
              type="button"
              onClick={downloadCsv}
              className="flex items-center bg-blue-500 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-150 ease-in-out"
            >
              <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
              CSV
            </button>
            <button
              id="btnXLS"
              type="button"
              onClick={downloadXls}
              className="flex items-center bg-green-500 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-150 ease-in-out"
            >
              <DocumentTextIcon className="h-5 w-5 mr-2" />
              XLS
            </button>
            <button
              id="btnCopyEmails"
              type="button"
              onClick={copyUsersInClipboard}
              className="flex items-center bg-purple-500 hover:bg-purple-700 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-150 ease-in-out"
            >
              <ClipboardDocumentListIcon className="h-5 w-5 mr-2" />
              CLIPBOARD EMAILS
            </button>
          </div>

          <div className="overflow-x-auto bg-white rounded-lg shadow-md scrollbar scrollbar-thumb-gray-400 scrollbar-track-gray-100">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Action Date</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredData.map((user) => (
                  <tr key={user.user} className="hover:bg-gray-50 transition-colors duration-150">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{user.user}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.cognitoEmail}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.lastAction}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mt-4" role="alert">
        <p className="font-bold">Information</p>
        <p>No users found for the selected date range.</p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-gray-100 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <label htmlFor="start-date" className="block text-sm font-medium text-gray-700 mb-1">
            Last Action Before
          </label>
          <input
            id="start-date"
            type="date"
            value={lastActionBeforeDate}
            onChange={(e) => setLastActionBeforeDate(e.target.value)}
            className="mt-1 block w-full sm:w-auto rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2"
          />
        </div>
        <div>
          {renderContent()}
        </div>
      </div>
    </div>
  );
}
